import { SortMode } from 'shared/hooks/use-data-grid-sorting';

import { LifeAreaColumnType, LifeAreaRowData } from './types';

type LifeAreaRowComparator = (
  rowA: LifeAreaRowData,
  rowB: LifeAreaRowData,
) => number;

// A constant for the equal case would make the sort result engine-defined.
const byNameThenId: LifeAreaRowComparator = (rowA, rowB) =>
  rowA.name.localeCompare(rowB.name) || rowA.id.localeCompare(rowB.id);

type LifeAreaRowCount = (row: LifeAreaRowData) => number;

const goalCount: LifeAreaRowCount = ({ goals }) => goals;
const projectCount: LifeAreaRowCount = ({ projects }) => projects;

// The tie-break stays ascending in both directions, never mirrored.
const ascendingByCount =
  (count: LifeAreaRowCount): LifeAreaRowComparator =>
  (rowA, rowB) =>
    count(rowA) - count(rowB) || byNameThenId(rowA, rowB);

const descendingByCount =
  (count: LifeAreaRowCount): LifeAreaRowComparator =>
  (rowA, rowB) =>
    count(rowB) - count(rowA) || byNameThenId(rowA, rowB);

// Empty visions sort last in both directions, never to the top.
const byVision = (
  rowA: LifeAreaRowData,
  rowB: LifeAreaRowData,
  compare: (visionA: string, visionB: string) => number,
) => {
  if (!rowA.visionDescription && !rowB.visionDescription) {
    return byNameThenId(rowA, rowB);
  }
  if (!rowA.visionDescription) {
    return 1;
  }
  if (!rowB.visionDescription) {
    return -1;
  }
  return (
    compare(rowA.visionDescription, rowB.visionDescription) ||
    byNameThenId(rowA, rowB)
  );
};

const ascendingComparators: Record<LifeAreaColumnType, LifeAreaRowComparator> =
  {
    [LifeAreaColumnType.Name]: byNameThenId,
    [LifeAreaColumnType.Vision]: (rowA, rowB) =>
      byVision(rowA, rowB, (visionA, visionB) =>
        visionA.localeCompare(visionB),
      ),
    [LifeAreaColumnType.Goals]: ascendingByCount(goalCount),
    [LifeAreaColumnType.Projects]: ascendingByCount(projectCount),
  };

const descendingComparators: Record<LifeAreaColumnType, LifeAreaRowComparator> =
  {
    [LifeAreaColumnType.Name]: (rowA, rowB) =>
      rowB.name.localeCompare(rowA.name) || byNameThenId(rowA, rowB),
    [LifeAreaColumnType.Vision]: (rowA, rowB) =>
      byVision(rowA, rowB, (visionA, visionB) =>
        visionB.localeCompare(visionA),
      ),
    [LifeAreaColumnType.Goals]: descendingByCount(goalCount),
    [LifeAreaColumnType.Projects]: descendingByCount(projectCount),
  };

export const sortLifeAreaRows = (
  rows: LifeAreaRowData[],
  column: LifeAreaColumnType | undefined,
  sortMode: SortMode,
): LifeAreaRowData[] => {
  if (!column || sortMode === SortMode.Custom) {
    return rows;
  }

  const comparators =
    sortMode === SortMode.Asc ? ascendingComparators : descendingComparators;

  return rows.toSorted(comparators[column]);
};
